<template>
  <net-item
    v-if="treeNode && sceneNode"
    class="scene-shape__container"
    :uid="nodeUid"
    :size="size"
    :node-uid="nodeUid"
    :class="{
      '-selected': selected,
      '-editable': editable
    }"
    :min-width="10"
    :min-height="10"
    :grid-size="1"
    :selected="selected"
    :edges="{
      top: true,
      right: true,
      bottom: true,
      left: true
    }"
    :locked="treeNode.locked"
    @resize="onResize"
    @move="onMove"
    @drag-start="onDragStart"
    @drag-end="onDragEnd"
    @select="select($event)"
  >
    <scene-shape-svg
      :path="cachedPath"
      :size="size"
      :fill="sceneNode.fill"
      :stroke="sceneNode.stroke"
      :stroke-width="sceneNode.strokeWidth"
      :dasharray="dasharray"
      :style="{
        overflow: editingShape ? 'visible' : 'hidden'
      }"
    >
      <template v-if="editingShape">
        <path
          :d="cachedPath"
          fill="none"
          stroke="transparent"
          :stroke-width="15 / scale"
          @pointerdown="onOutlinePointerDown($event)"
        />
      </template>

      <g v-if="editingRadius && editable">
        <!-- radius control for rounded rectangle -->
        <path
          v-if="data && data.shapeType === 'roundedRectangle'"
          :d="`M${radius},${-8 / scale} L${radius + 8 / scale},0 L${radius},${8 / scale} L${radius - 8 / scale},0 Z`"
          fill="yellow"
          stroke="black"
          class="scene-shape__radius-control"
          :stroke-width="1 / scale"
          @pointerdown="startRadiusDrag($event)"
        />
      </g>

      <g v-if="editingShape">
        <g v-for="shape in pathShapes">
          <template v-for="(point, index) in shape.points" :key="index">
            <template v-if="point.left">
              <line
                :x1="point.main.x"
                :y1="point.main.y"
                :x2="point.left.x"
                :y2="point.left.y"
                stroke="red"
                :stroke-width="1 / scale"
              />
              <circle
                class="editing-handle"
                :cx="point.left.x"
                :cy="point.left.y"
                :r="4 / scale"
                fill="var(--color-action)"
                stroke="var(--color-background)"
                :stroke-width="1 / scale"
                @pointerdown="
                  startBezierPointDrag($event, shape, point, 'left')
                "
                @contextmenu="
                  onBezierPointContextMenu($event, shape, point, 'left')
                "
                v-on-long-press="[activateContextMenu, { delay: 2000 }]"
              />
            </template>
            <template v-if="point.right">
              <line
                :x1="point.main.x"
                :y1="point.main.y"
                :x2="point.right.x"
                :y2="point.right.y"
                stroke="red"
                :stroke-width="1 / scale"
              />
              <circle
                class="editing-handle"
                :cx="point.right.x"
                :cy="point.right.y"
                :r="4 / scale"
                fill="var(--color-action)"
                stroke="var(--color-background)"
                :stroke-width="1 / scale"
                @pointerdown="
                  startBezierPointDrag($event, shape, point, 'right')
                "
                @contextmenu="
                  onBezierPointContextMenu($event, shape, point, 'right')
                "
                v-on-long-press="[activateContextMenu, { delay: 2000 }]"
              />
            </template>
            <circle
              class="editing-handle"
              :cx="point.main.x"
              :cy="point.main.y"
              :r="6 / scale"
              fill="var(--color-action)"
              stroke="var(--color-background)"
              :stroke-width="1 / scale"
              @pointerdown="startPointerDrag($event, shape, point)"
              @contextmenu="onPointContextMenu($event, shape, point)"
              v-on-long-press="[activateContextMenu, { delay: 2000 }]"
            />
          </template>
        </g>
      </g>
    </scene-shape-svg>

    <!-- point context menu -->
    <context-menu
      v-if="pointContextMenu"
      :x="pointContextMenu.position.x"
      :y="pointContextMenu.position.y"
      @close="pointContextMenu = null"
    >
      <button
        class="context-menu__entry"
        @click="deletePoint(pointContextMenu.shape, pointContextMenu.point)"
      >
        <lucide-icon name="trash-2" />
        {{ $t("actions.deletePoint") }}
      </button>
      <button
        class="context-menu__entry"
        @click="smoothPoint(pointContextMenu.shape, pointContextMenu.point)"
      >
        <lucide-icon name="square-round-corner" />
        {{ $t("actions.addBeziers") }}
      </button>
      <button
        v-if="pointContextMenu.point.left || pointContextMenu.point.right"
        class="context-menu__entry"
        @click="straightenPoint(pointContextMenu.shape, pointContextMenu.point)"
      >
        <lucide-icon name="pyramid" />
        {{ $t("actions.removeBeziers") }}
      </button>
    </context-menu>

    <!-- bezier point context menu -->
    <context-menu
      v-if="bezierPointContextMenu"
      :x="bezierPointContextMenu.position.x"
      :y="bezierPointContextMenu.position.y"
      @close="bezierPointContextMenu = null"
    >
      <button
        class="context-menu__entry"
        @click="
          deleteBezierPoint(
            bezierPointContextMenu.shape,
            bezierPointContextMenu.point,
            bezierPointContextMenu.side
          )
        "
      >
        <lucide-icon name="trash-2" />
        {{ $t("actions.deletePoint") }}
      </button>
    </context-menu>
  </net-item>
</template>

<script lang="ts" setup>
import { ref, computed, inject, watch, onMounted } from "vue";
import { eventHub } from "@/helpers/utility/Events";
import { vOnLongPress } from "@vueuse/components";
import { last } from "lodash-es";
import { rotatePoint } from "@/helpers/math/geometry";
import SVGParser from "./SVGParser";

import NetItem from "@editor/net/NetItem.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import SceneShapeSvg from "./SceneShapeSvg.vue";

import { useUndo } from "@editor/store/undo/undo.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { createNewShapePath, resizeShapePath } from "./sceneShapeFunctions";
import { keyCanvasDimensions } from "@editor/net/netCanvasInjectionKeys";
import { useNetCoords } from "@editor/net/netCoordsModifier";
import { useSceneTree } from "../scene/sceneTree";
import { useSceneNodesStore } from "../scene/sceneNodes.store";

import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type PointCurveShape from "./SVGParser/PointCurveShape";
import type Point from "./SVGParser/Point";
import type { NetToolKey } from "@editor/net/NetTools";
import type { Uid } from "@/helpers/utility/Id";
import type { RotatableRect } from "../scene/SceneNodeItem";
import type { SceneTreeNode } from "../scene/SceneTreeItem";
import { onUnmounted } from "vue";

const props = withDefaults(
  defineProps<{
    nodeUid: Uid<"sceneNode">;
    size: RotatableRect;
    editable?: boolean;
    moveable?: boolean;
    resizable?: boolean;
    selection: CanvasSelection;
    tool: NetToolKey;
  }>(),
  {
    editable: false,
    moveable: true,
    resizable: true
  }
);

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"sceneNode"> }): void;
  (e: "wheel", event: WheelEvent): void;
  (
    e: "move",
    delta: { x: number; y: number; gridX: number; gridY: number }
  ): void;
  (e: "set-tool", tool: NetToolKey): void;
}>();

const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();
const canvasDimensions = inject(keyCanvasDimensions);

const { toLocal } = useNetCoords({ canvasDimensions });

const scale = computed(() => {
  return canvasDimensions?.value?.scale || 1;
});

const rotation = computed(() => {
  return props.size.rotation || 0;
});

const treeNode = computed(() => {
  return sceneTree.treeNodes.get(props.nodeUid) as SceneTreeNode<"shape"> | undefined;
});
const sceneNode = computed(() => treeNode.value?.node ?? null);
const data = computed(() => sceneNode.value?.data);

const dasharray = computed(() => {
  const strokeStyle = sceneNode.value?.strokeStyle;
  if (strokeStyle === "dashed") {
    return "4, 2"; // Example dashed style
  } else if (strokeStyle === "dotted") {
    return "1, 2"; // Example dotted style
  } else {
    return ""; // Solid line
  }
});

onMounted(() => {
  console.log("shape node", sceneNode.value);
})

// ====================
// Selection handling

const selectionStore = useSelectionStore();

const selected = computed(() => {
  return selectionStore.selectedItems.some(
    (item) => item.type == "sceneNode" && item.id == sceneNode.value?.id
  );
});

function select(item: { uid: Uid<"sceneNode"> }) {
  if (!props.editable) return;
  emit("select", item);
}

// ====================
// Editing shape handling

const dragging = ref(false);

const cachedPath = ref<string>(sceneNode.value?.data.path || "");

const editingShape = computed(() => {
  return props.tool === "shapeedit";
});

const pathShapes = ref<PointCurveShape[]>([]);

watch(selected, (isSelected) => {
  if (!isSelected) {
    // If the shape is deselected, reset the editing state
    if (props.tool === "shapeedit") {
      emit("set-tool", "pointer");
    }
    if (props.tool === "radius") {
      emit("set-tool", "pointer");
    }
    cachedPath.value = sceneNode.value?.data.path || "";
    pathShapes.value = [];
  }
});

watch(
  () => sceneNode.value?.data.path,
  (newPath) => {
    // Update the cached path when the net shape path changes
    cachedPath.value = newPath || "";
    if (editingShape.value) {
      // If we are editing the shape, rebuild the segments
      buildShapeSegments();
    }
  }
);

function editShape() {
  if (editingShape.value) {
    emit("set-tool", "pointer");
  } else {
    buildShapeSegments();
    emit("set-tool", "shapeedit");
  }
}

function onSceneShapeEditRequested(nodeUid: string) {
  // Handle external requests to edit a shape
  if (props.nodeUid === nodeUid) {
    editShape();
  }
}

eventHub.on("scene-shape-edit-requested", onSceneShapeEditRequested);
onUnmounted(() => {
  eventHub.off("scene-shape-edit-requested", onSceneShapeEditRequested);
});

function buildShapeSegments() {
  // Convert the SVG path data into segments for editing
  const pathData = sceneNode.value?.data.path || "";
  cachedPath.value = pathData;

  const outline = SVGParser.parse(pathData);

  const shapes = outline.getShapes();
  pathShapes.value = shapes;
}

const lastPointerDownTime = ref(0);
const lastPointerDownPoint = ref<Point | null>(null);

let pointDragStartX = 0;
let pointDragStartY = 0;

function startPointerDrag(
  event: PointerEvent,
  shape: PointCurveShape,
  point: Point
) {
  // Start dragging a point of the shape
  event.stopPropagation();
  event.preventDefault();

  const now = Date.now();

  if (now - lastPointerDownTime.value < 200) {
    console.log("Double tap detected");
    if (point == lastPointerDownPoint.value) {
      deletePoint(shape, point);
      return;
    }
  }

  dragging.value = true;

  lastPointerDownTime.value = now;
  lastPointerDownPoint.value = point;

  const startX = event.clientX;
  const startY = event.clientY;

  pointDragStartX = point.main.x;
  pointDragStartY = point.main.y;

  const pointLeftStartX = point.left ? point.left.x : 0;
  const pointLeftStartY = point.left ? point.left.y : 0;
  const pointRightStartX = point.right ? point.right.x : 0;
  const pointRightStartY = point.right ? point.right.y : 0;

  const onPointerMove = (moveEvent: PointerEvent) => {
    const fixedClientPoint = rotatePoint(
      {
        x: moveEvent.clientX,
        y: moveEvent.clientY
      },
      -rotation.value,
      {
        x: startX,
        y: startY
      }
    );
    const deltaX = fixedClientPoint.x - startX;
    const deltaY = fixedClientPoint.y - startY;

    if (moveEvent.shiftKey) {
      // If shift is pressed, move the point in a straight line in 90-degree increments
      // from the original point position
      const angle = Math.atan2(deltaY, deltaX);
      const angleInDegrees = (angle * 180) / Math.PI;
      const snappedAngle = Math.round(angleInDegrees / 90) * 90;
      const snappedDeltaX =
        Math.cos((snappedAngle * Math.PI) / 180) *
        Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const snappedDeltaY =
        Math.sin((snappedAngle * Math.PI) / 180) *
        Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      point.main.x = pointDragStartX + snappedDeltaX / scale.value;
      point.main.y = pointDragStartY + snappedDeltaY / scale.value;

      if (point.left) {
        point.left.x = pointLeftStartX + snappedDeltaX / scale.value;
        point.left.y = pointLeftStartY + snappedDeltaY / scale.value;
      }
      if (point.right) {
        point.right.x = pointRightStartX + snappedDeltaX / scale.value;
        point.right.y = pointRightStartY + snappedDeltaY / scale.value;
      }
    } else {
      // Update the point position
      point.main.x = pointDragStartX + deltaX / scale.value;
      point.main.y = pointDragStartY + deltaY / scale.value;

      if (point.left) {
        point.left.x = pointLeftStartX + deltaX / scale.value;
        point.left.y = pointLeftStartY + deltaY / scale.value;
      }
      if (point.right) {
        point.right.x = pointRightStartX + deltaX / scale.value;
        point.right.y = pointRightStartY + deltaY / scale.value;
      }
    }

    // Redraw the shape
    const updatedPath = pathFromShapes();
    cachedPath.value = updatedPath;
  };

  const onPointerUp = () => {
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);

    dragging.value = false;

    updatePath();

    // End the drag action
    useUndo().endGroup("model");
  };

  // Start the drag action
  useUndo().startGroup("model", "moveText");

  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
}

function startBezierPointDrag(
  event: PointerEvent,
  shape: PointCurveShape,
  point: Point,
  side: "left" | "right"
) {
  // Start dragging a side point of the shape
  event.stopPropagation();
  event.preventDefault();

  const startX = event.clientX;
  const startY = event.clientY;

  const pointStartX = (side === "left" ? point.left?.x : point.right?.x) || 0;
  const pointStartY = (side === "left" ? point.left?.y : point.right?.y) || 0;

  const oppositePointStartX =
    (side === "left" ? point.right?.x : point.left?.x) || 0;
  const oppositePointStartY =
    (side === "left" ? point.right?.y : point.left?.y) || 0;

  dragging.value = true;

  const onPointerMove = (moveEvent: PointerEvent) => {
    const fixedClientPoint = rotatePoint(
      {
        x: moveEvent.clientX,
        y: moveEvent.clientY
      },
      -rotation.value,
      {
        x: startX,
        y: startY
      }
    );
    const deltaX = fixedClientPoint.x - startX;
    const deltaY = fixedClientPoint.y - startY;

    const mainPointX = point.main.x;
    const mainPointY = point.main.y;

    if (moveEvent.shiftKey) {
      // Snap handle to 45-degree increments from the main point
      const mouseX = pointStartX + deltaX / scale.value;
      const mouseY = pointStartY + deltaY / scale.value;
      const dx = mouseX - mainPointX;
      const dy = mouseY - mainPointY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      let angle = Math.atan2(dy, dx);
      // Snap angle to nearest 45deg (PI/4 radians)
      const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);

      const snappedX = mainPointX + Math.cos(snappedAngle) * distance;
      const snappedY = mainPointY + Math.sin(snappedAngle) * distance;

      if (side === "left" && point.left) {
        point.left.x = snappedX;
        point.left.y = snappedY;
      } else if (side === "right" && point.right) {
        point.right.x = snappedX;
        point.right.y = snappedY;
      }
    } else if (moveEvent.ctrlKey || moveEvent.metaKey) {
      // Holding the control key locks the point in the angle
      // it has relative to the main point
      const angle = Math.atan2(
        pointStartY - mainPointY,
        pointStartX - mainPointX
      );
      // Snap the new position to the angle
      const mouseX = pointStartX + deltaX / scale.value;
      const mouseY = pointStartY + deltaY / scale.value;
      const dx = mouseX - mainPointX;
      const dy = mouseY - mainPointY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const snappedX = mainPointX + Math.cos(angle) * distance;
      const snappedY = mainPointY + Math.sin(angle) * distance;
      if (side === "left" && point.left) {
        point.left.x = snappedX;
        point.left.y = snappedY;
      } else if (side === "right" && point.right) {
        point.right.x = snappedX;
        point.right.y = snappedY;
      }
    } else {
      if (side === "left" && point.left) {
        point.left.x = pointStartX + deltaX / scale.value;
        point.left.y = pointStartY + deltaY / scale.value;
      } else if (side === "right" && point.right) {
        point.right.x = pointStartX + deltaX / scale.value;
        point.right.y = pointStartY + deltaY / scale.value;
      }
    }

    if (moveEvent.altKey && point.left && point.right) {
      // If alt is pressed, move the opposite point as well,
      // mirroring the position of the dragged point
      if (side === "left") {
        const xMainDistance = point.left.x - point.main.x;
        const yMainDistance = point.left.y - point.main.y;
        point.right.x = point.main.x - xMainDistance;
        point.right.y = point.main.y - yMainDistance;
      } else if (side === "right") {
        const xMainDistance = point.right.x - point.main.x;
        const yMainDistance = point.right.y - point.main.y;
        point.left.x = point.main.x - xMainDistance;
        point.left.y = point.main.y - yMainDistance;
      }
    } else {
      // If alt is not pressed, ensure the opposite point
      // stays in place
      if (side === "left" && point.right) {
        point.right.x = oppositePointStartX;
        point.right.y = oppositePointStartY;
      } else if (side === "right" && point.left) {
        point.left.x = oppositePointStartX;
        point.left.y = oppositePointStartY;
      }
    }

    // Redraw the shape
    const updatedPath = pathFromShapes();
    cachedPath.value = updatedPath;
  };

  const onPointerUp = () => {
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("keyup", onKeyUp);
    document.removeEventListener("keydown", onKeyDown);

    dragging.value = false;

    updatePath();
    // End the drag action
    useUndo().endGroup("model");
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      // If escape is pressed, cancel the drag
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("keydown", onKeyDown);

      dragging.value = false;
      return;
    }

    if (event.key === "Alt") {
      // If alt is released, ensure the opposite point stays in place
      if (side === "left" && point.right) {
        point.right.x = oppositePointStartX;
        point.right.y = oppositePointStartY;
      } else if (side === "right" && point.left) {
        point.left.x = oppositePointStartX;
        point.left.y = oppositePointStartY;
      }

      // Redraw the shape
      const updatedPath = pathFromShapes();
      cachedPath.value = updatedPath;

      event.preventDefault();
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key == "Alt") {
      // If alt is pressed, mirror the opposite point
      if (side === "left" && point.right && point.left) {
        const xMainDistance = point.left.x - point.main.x;
        const yMainDistance = point.left.y - point.main.y;
        point.right.x = point.main.x - xMainDistance;
        point.right.y = point.main.y - yMainDistance;
      } else if (side === "right" && point.right && point.left) {
        const xMainDistance = point.right.x - point.main.x;
        const yMainDistance = point.right.y - point.main.y;
        point.left.x = point.main.x - xMainDistance;
        point.left.y = point.main.y - yMainDistance;
      }

      // Redraw the shape
      const updatedPath = pathFromShapes();
      cachedPath.value = updatedPath;

      event.preventDefault();
    }
  };

  // Start the drag action
  useUndo().startGroup("model", "moveText");

  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("keyup", onKeyUp);
  document.addEventListener("keydown", onKeyDown);
}

function pathFromShapes(): string {
  // Convert the path shapes back to a SVG path string
  const paths = pathShapes.value.map((shape, index) => {
    return shape.toSVG();
  });
  return paths.join(" ");
}

function getRefittedShape(): {
  path: string;
  width: number;
  height: number;
  x: number;
  y: number;
} {
  const { offsetX, offsetY } = fixShapeOffsets(pathShapes.value[0]);

  const bbox = getShapeBBox(pathShapes.value[0]);

  const node = sceneNode.value;
  if (!node) {
    throw new Error("Scene node not found for shape");
  }

  return {
    path: pathFromShapes(),
    width: bbox.width,
    height: bbox.height,
    x: node.x + offsetX,
    y: node.y + offsetY
  };
}

function fixShapeOffsets(shape: PointCurveShape): {
  shape: PointCurveShape;
  offsetX: number;
  offsetY: number;
} {
  const bbox = getShapeBBox(shape);
  const offsetX = bbox.x;
  const offsetY = bbox.y;

  const handledPoints = new Set<Point>();

  if (offsetX != 0 || offsetY != 0) {
    shape.points.forEach((point) => {
      // Skip already handled points
      if (handledPoints.has(point)) return;
      handledPoints.add(point);

      point.main.x -= offsetX;
      point.main.y -= offsetY;
      if (point.left) {
        point.left.x -= offsetX;
        point.left.y -= offsetY;
      }
      if (point.right) {
        point.right.x -= offsetX;
        point.right.y -= offsetY;
      }
    });
  }

  return {
    shape,
    offsetX,
    offsetY
  };
}

function getShapeBBox(shape: PointCurveShape): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const bbox = {
    x: Infinity,
    y: Infinity,
    width: -Infinity,
    height: -Infinity
  };

  shape.points.forEach((point) => {
    bbox.x = Math.min(bbox.x, point.main.x);
    bbox.y = Math.min(bbox.y, point.main.y);
    bbox.width = Math.max(bbox.width, point.main.x);
    bbox.height = Math.max(bbox.height, point.main.y);

    if (point.left) {
      bbox.x = Math.min(bbox.x, point.left.x);
      bbox.y = Math.min(bbox.y, point.left.y);
      bbox.width = Math.max(bbox.width, point.left.x);
      bbox.height = Math.max(bbox.height, point.left.y);
    }

    if (point.right) {
      bbox.x = Math.min(bbox.x, point.right.x);
      bbox.y = Math.min(bbox.y, point.right.y);
      bbox.width = Math.max(bbox.width, point.right.x);
      bbox.height = Math.max(bbox.height, point.right.y);
    }
  });

  return {
    x: bbox.x,
    y: bbox.y,
    width: bbox.width,
    height: bbox.height
  };
}

let lastOutlinePointerDownTime = 0;
let lastOutlinePointerDownPosition: { x: number; y: number } | null = null;

function onOutlinePointerDown(event: PointerEvent) {
  if (
    lastOutlinePointerDownPosition &&
    Date.now() - lastOutlinePointerDownTime < 200 &&
    Math.abs(event.clientX - lastOutlinePointerDownPosition.x) < 20 &&
    Math.abs(event.clientY - lastOutlinePointerDownPosition.y) < 20
  ) {
    // Double click on the outline, add a new point
    console.log("Double click on outline, adding new point");
    const lastShape = last(pathShapes.value);
    if (!lastShape) return;

    const center = {
      x: props.size.x + props.size.width / 2,
      y: props.size.y + props.size.height / 2
    };

    const clientPoint = {
      x: event.clientX,
      y: event.clientY
    };

    const mouseLocalPoint = toLocal(clientPoint);

    const point = rotatePoint(mouseLocalPoint, -rotation.value, center);

    const offsetPoint = {
      x: point.x - props.size.x,
      y: point.y - props.size.y
    };

    lastShape.splitAtPoint(offsetPoint.x, offsetPoint.y);

    updatePath();
    return;
  }

  lastOutlinePointerDownTime = Date.now();
  lastOutlinePointerDownPosition = { x: event.clientX, y: event.clientY };
}

function deletePoint(shape: PointCurveShape, point: Point) {
  // Remove the point from the shape
  const indices = shape.points
    .map((p, i) => ({ index: i, point: p }))
    .filter((p) => p.point === point)
    .map((p) => p.index);
  if (indices.length == 0) {
    console.warn("Point not found in shape points");
    return;
  }
  for (let i = indices.length - 1; i >= 0; i--) {
    const index = indices[i];
    shape.points.splice(index, 1);
  }

  const node = sceneNode.value;
  if (!node) {
    throw new Error("Scene node not found for shape");
  }

  if (shape.points.length < 2) {
    // If the shape has less than 2 points, remove it
    console.log("Shape has less than 2 points, removing shape");
    sceneNodeStore.delete(node);
  } else {
    updatePath();
  }
}

function deleteBezierPoint(
  shape: PointCurveShape,
  point: Point,
  side: "left" | "right"
) {
  // Remove the bezier point from the shape
  if (side === "left") {
    point.left = null;
  } else {
    point.right = null;
  }

  updatePath();
}

function straightenPoint(shape: PointCurveShape, point: Point) {
  // Remove the bezier points from the shape
  point.left = null;
  point.right = null;

  updatePath();
}

function smoothPoint(shape: PointCurveShape, point: Point) {
  const index = shape.points.indexOf(point);
  const prevIndex = index > 0 ? index - 1 : shape.points.length - 1;
  const prevPoint = shape.points[prevIndex];
  const nextIndex = (index + 1) % shape.points.length;
  const nextPoint = shape.points[nextIndex];
  const distanceToNeighbours = Math.min(
    pointDistance(point.main, prevPoint.main),
    pointDistance(point.main, nextPoint.main)
  );
  const distance = Math.max(20 / scale.value, distanceToNeighbours / 3); // Distance for bezier points

  const noBeziers = !point.left && !point.right;

  // Add bezier points to the shape
  if (!point.left || pointDistance(point.main, point.left) < distance) {
    const bezierPoint = {
      x: point.main.x,
      y: point.main.y
    };
    // Get the best angle for the bezier point
    if (noBeziers || !point.right) {
      // If no right point, place it dependent of the neighboring points
      const angle = Math.atan2(
        nextPoint.main.y - prevPoint.main.y,
        nextPoint.main.x - prevPoint.main.x
      );
      bezierPoint.x -= Math.cos(angle) * distance;
      bezierPoint.y -= Math.sin(angle) * distance;
    } else if (point.right) {
      const angle = Math.atan2(
        point.right.y - point.main.y,
        point.right.x - point.main.x
      );
      bezierPoint.x -= Math.cos(angle) * distance;
      bezierPoint.y -= Math.sin(angle) * distance;
    }

    point.setLeft(bezierPoint.x, bezierPoint.y);
  }
  if (!point.right || pointDistance(point.main, point.right) < distance) {
    const bezierPoint = {
      x: point.main.x,
      y: point.main.y
    };

    // Get the best angle for the bezier point
    if (noBeziers || !point.left) {
      // If no left point, place it dependent of the neighboring points
      const angle = Math.atan2(
        prevPoint.main.y - nextPoint.main.y,
        prevPoint.main.x - nextPoint.main.x
      );
      bezierPoint.x -= Math.cos(angle) * distance;
      bezierPoint.y -= Math.sin(angle) * distance;
    } else if (point.left) {
      const angle = Math.atan2(
        point.left.y - point.main.y,
        point.left.x - point.main.x
      );
      bezierPoint.x -= Math.cos(angle) * distance;
      bezierPoint.y -= Math.sin(angle) * distance;
    }

    point.setRight(bezierPoint.x, bezierPoint.y);
  }

  updatePath();
}

function pointDistance(
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

function updatePath() {
  // Redraw the shape
  const updatedPath = pathFromShapes();
  cachedPath.value = updatedPath;

  // Update the net shape with the new path
  const { path, width, height, x, y } = getRefittedShape();
  cachedPath.value = path;

  const node = sceneNode.value;
  if (!node) {
    throw new Error("Scene node not found for shape");
  }

  const rotationOffset = getRotationOffset(
    { x: node.x, y: node.y },
    {
      x: node.x,
      y: node.y,
      width: node.width,
      height: node.height
    },
    {
      x: x,
      y: y,
      width: width,
      height: height
    }
  );

  sceneNodeStore.update({
    id: node.id,
    width: width,
    height: height,
    x: x - rotationOffset.x,
    y: y - rotationOffset.y,
    data: {
      ...node.data,
      path: path,
      shapeType: "polygon" // edited shapes are always polygons
    }
  });
}

function getRotationOffset(
  point: { x: number; y: number },
  dimensionsBefore: { x: number; y: number; width: number; height: number },
  dimensionsAfter: { x: number; y: number; width: number; height: number }
) {
  const centerBefore = {
    x: dimensionsBefore.x + dimensionsBefore.width / 2,
    y: dimensionsBefore.y + dimensionsBefore.height / 2
  };
  const centerAfter = {
    x: dimensionsAfter.x + dimensionsAfter.width / 2,
    y: dimensionsAfter.y + dimensionsAfter.height / 2
  };
  const pointBefore = rotatePoint(point, rotation.value, centerBefore);
  const pointAfter = rotatePoint(point, rotation.value, centerAfter);
  return {
    x: pointAfter.x - pointBefore.x,
    y: pointAfter.y - pointBefore.y
  };
}

// ====================
// Radius control handling

const radius = computed(() => {
  // Get the current radius of the shape
  return Math.min(data.value?.radius || 0, maxRadius.value);
});

const maxRadius = computed(() => {
  // Calculate the maximum radius based on the shape dimensions
  const width = sceneNode.value?.width || 0;
  const height = sceneNode.value?.height || 0;
  return Math.min(width, height) / 2;
});

const editingRadius = computed(() => {
  return props.tool === "radius";
});

function startRadiusDrag(event: PointerEvent) {
  // Start dragging the radius control for rounded rectangles
  event.stopPropagation();
  event.preventDefault();

  const startX = event.clientX;
  const startY = event.clientY;

  const radiusStart = radius.value;

  dragging.value = true;

  const onPointerMove = (moveEvent: PointerEvent) => {
    const rotationCorrectedPoint = rotatePoint(
      {
        x: moveEvent.clientX,
        y: moveEvent.clientY
      },
      -rotation.value,
      {
        x: startX,
        y: startY
      }
    );
    const deltaX = rotationCorrectedPoint.x - startX;

    // Update the radius based on the mouse movement
    const newRadius = Math.min(
      Math.max(0, radiusStart + deltaX / scale.value),
      maxRadius.value
    );

    const shapeData = data.value;
    if (!shapeData) return;

    const node = sceneNode.value;
    if (!node) return;

    const path = createNewShapePath(
      shapeData.shapeType,
      node.width,
      node.height,
      newRadius
    );
    cachedPath.value = path;
    sceneNodeStore.updateData<"shape">(node.id, {
      radius: newRadius,
      path: path
    });
  };

  const onPointerUp = () => {
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);

    dragging.value = false;

    // End the drag action
    useUndo().endGroup("model");
  };

  // Start the drag action
  useUndo().startGroup("model", "moveText");

  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
}

// ====================
// Point context menu handling

const pointContextMenu = ref<{
  shape: PointCurveShape;
  point: Point;
  position: { x: number; y: number };
} | null>(null);

const onPointContextMenu = (
  event: MouseEvent | PointerEvent,
  shape: PointCurveShape,
  point: Point
) => {
  event.preventDefault();
  event.stopPropagation();

  pointContextMenu.value = {
    shape,
    point,
    position: { x: event.clientX, y: event.clientY }
  };
};

const bezierPointContextMenu = ref<{
  shape: PointCurveShape;
  point: Point;
  side: "left" | "right";
  position: { x: number; y: number };
} | null>(null);

function onBezierPointContextMenu(
  event: MouseEvent,
  shape: PointCurveShape,
  point: Point,
  side: "left" | "right"
) {
  event.preventDefault();
  event.stopPropagation();

  bezierPointContextMenu.value = {
    shape,
    point,
    side,
    position: { x: event.clientX, y: event.clientY }
  };
}

// ====================
// Drag and drop handling

function onDragStart() {
  // Group the action to make sure the movement of all
  // targets can be undone at once
  useUndo().startGroup("model", "moveText");
}

function onDragEnd() {
  // Close the drag action undo group, including
  // the area update
  useUndo().endGroup("model");
}

// ====================
// Resize and move handling

function onResize(newSize: {
  x: number;
  y: number;
  height: number;
  width: number;
}) {
  const { width, height, radius } = updateResizedShape(newSize);

  const node = sceneNode.value;
  if (!node) {
    throw new Error("Scene node not found for shape");
  }

  sceneNodeStore.update({
    id: node.id,
    x: newSize.x,
    y: newSize.y,
    width: width,
    height: height,
    data: {
      ...node.data,
      path: cachedPath.value,
      radius: radius
    }
  });
}

function updateResizedShape(newSize: { width: number; height: number }) {
  const width = Math.max(newSize.width, 10);
  const height = Math.max(newSize.height, 10);

  const node = sceneNode.value;
  if (!node) {
    throw new Error("Scene node not found for shape");
  }
  const shapeData = data.value;
  if (!shapeData) {
    throw new Error("Scene node data not found for shape");
  }

  const radius = Math.min(
    shapeData.radius || 0,
    Math.min(width, height) / 2
  );

  if (shapeData.shapeType == "polygon") {
    // Only polygon shapes need to be rescaled,
    // all other shapes are easier recreated.
    const rescaledPath = resizeShapePath(
      shapeData.path,
      node.width,
      node.height,
      width,
      height
    );
    cachedPath.value = rescaledPath;
  } else {
    // We recreate other shapes, ensuring their proportions are adapted
    // to the new dimensions.
    cachedPath.value = createNewShapePath(
      shapeData.shapeType,
      width,
      height,
      radius
    );
  }

  return {
    width,
    height,
    radius
  };
}

watch(
  () => props.size,
  (newSize) => {
    // When the size prop changes, update the shape
    updateResizedShape(newSize);
  },
  {
    deep: true
  }
);

function onMove(delta: { x: number; y: number; gridX: number; gridY: number }) {
  emit("move", delta);
}

function activateContextMenu(event: MouseEvent) {
  // Trigger a context menu click on the element
  event.preventDefault();
  event.stopPropagation();

  const element = event.target as HTMLElement;
  if (element) {
    element.dispatchEvent(
      new MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        clientX: event.clientX,
        clientY: event.clientY
      })
    );
  }
}
</script>

<style lang="scss">
.scene-shape {
  position: absolute;
  display: block;

  &__container {
    pointer-events: none;

    &.-editable {
      pointer-events: auto;
    }
  }

  .net-item__main {
    position: relative;
  }

  &__radius-control {
    cursor: ew-resize;
  }

  .is-selected > & {
    outline: 1px dotted var(--color-action);
    outline-offset: -10px;
  }
}
</style>
